/* eslint-disable @typescript-eslint/no-unused-vars */
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { useCallback, useState } from "react";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { processError } from "../../../../common/utils/base_error";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  QualidadeAcoesPreventivasForm,
  QualidadeAcoesPreventivasSchema,
} from "../schemas/AcoesMelhorias/qualidade_acoes_melhorias";
import { AcoePreventivasLista } from "../../domain/models/acoes_melhorias_lista";
import { evaluateParamValue } from "../../../../common/utils/params_utils";
import { useDropDownLoader } from "../../../../common/hooks/use_dropdown_loader";
import { useNavigate, useParams } from "react-router";
import { useAcoesPreventivasFilters } from "../hooks/Qualidade_acoes_preventivas/filter";
import { useAcoesPreventivasPagination } from "../hooks/Qualidade_acoes_preventivas/pagination";
import {
  AcaoPrevCorretivaForm,
  AcaoPrevCorretivaSchema,
} from "../schemas/AcaoPrevCorretiva/qualidade_acoes_melhorias";
import { AcaoPrevCorretivasLista } from "../../domain/models/acao_prev_corretiva_lista";
import { useAcaoPrevCorretivaPagination } from "../hooks/Qualidade_acoes_prev_corretiva/pagination";
import { useChangeHistory } from "../../../../common/hooks/use_change_history";
import { LogTipoTabela } from "../../../../common/utils/log_tabela_tipo";

type LoadAcaoPrevCorretivaListaFuncState = {
  IdAcaoQualidade?: number;
  page?: number;
  rowsPerPage?: number;
};

export const useQualidadeCadastroAcoesPreventivasViewModel = () => {
  const {
    commons: { getAllLogAlteracaoUseCase, getAllLogAlteracaoByParentUseCase },
    qualidade: {
      createAcoesPreventivasUseCase,
      updateAcoesPreventivasUseCase,
      createAcaoPreventivaCorretivaUseCase,
      getAllListagemAcaoPrevCorretivasUseCase,
      updateAcaoPrevCorretivasUseCase,
      deleteAcaoPrevCorretivasUseCase,
      getAllListagemAcoesMelhoriasUseCase,
      getArquivoUseCase,
    },
  } = useDependencyInjection();

  const { loadCentroCustoDropDown } = useDropDownLoader();
  const changeHistory = useChangeHistory({
    getAllLogAlteracaoByParentUseCase: getAllLogAlteracaoByParentUseCase,
    getAllLogAlteracaoUseCase: getAllLogAlteracaoUseCase,
    onSetError: (e) => pushErrorNotification("Erro", [e]),
  });

  const hookForm = useForm<QualidadeAcoesPreventivasForm>({
    mode: "onSubmit",
    reValidateMode: "onSubmit",
    resolver: zodResolver(QualidadeAcoesPreventivasSchema),
    defaultValues: {
      Tipo: 0,
      Data: new Date(),
      AcaoAberta: 1,
    },
  });
  const hookFormPrev = useForm<AcaoPrevCorretivaForm>({
    mode: "onSubmit",
    reValidateMode: "onSubmit",
    resolver: zodResolver(AcaoPrevCorretivaSchema),
    defaultValues: {
      Tipo: "P",
    },
  });

  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>();
  const [acoesPreventivasLista, setAcoesPreventivasLista] = useState<
    AcoePreventivasLista[]
  >([]);
  const [selectecoesPreventiva, setSelectedAcoesPreventiva] =
    useState<AcoePreventivasLista>();
  const [idAcaoQualidade, setIdAcaoQualidade] = useState<number>(0);
  const [acaoPrevCorretivaLista, setAcaoPrevCorretivaLista] = useState<
    AcaoPrevCorretivasLista[]
  >([]);
  const [acaoCorretivaLista, setAcaoCorretivaLista] = useState<
    AcaoPrevCorretivasLista[]
  >([]);
  const [loadingPrev, setLoadingPrev] = useState<boolean>(false);
  //Arquivo
  const [openDeleteArquivoModal, setOpenDeleteArquivoModal] =
    useState<boolean>(false);
  const [limparArquivo, setLimparArquivo] = useState<string>();
  const [arquivo, setArquivo] = useState<string>();

  const {
    pushErrorNotification,
    pushInfoNotification,
    pushWarnNotification,
    setCustomLoading,
  } = usePageLoader();
  const navigate = useNavigate();

  const filters = useAcoesPreventivasFilters();
  const pagination = useAcoesPreventivasPagination();
  const paginationPrev = useAcaoPrevCorretivaPagination();
  const paginationCorretivas = useAcaoPrevCorretivaPagination();
  const { tipo, id } = useParams();

  // #region Funções de carregamento

  const loadAcoesPrevCorretivaListaState = useCallback(
    async (funcState?: LoadAcaoPrevCorretivaListaFuncState) => {
      try {
        const filterIdAcaoQualidade = evaluateParamValue(
          funcState?.IdAcaoQualidade,
          filters.IdAcaoQualidade,
        );
        const filteredPage =
          funcState?.page ?? paginationPrev.page ?? paginationCorretivas.page;
        const filteredRowsPerPage =
          funcState?.rowsPerPage ??
          paginationPrev.rowsPerPage ??
          paginationCorretivas.rowsPerPage;

        if (filterIdAcaoQualidade) {
          await getAllListagemAcaoPrevCorretivasUseCase.getAllListagemAcaoPrevCorretivas(
            {
              IdAcaoQualidade: filterIdAcaoQualidade,
            },
            filteredPage,
            filteredRowsPerPage,
          );

          // 2. Captura a lista bruta mista
          const todosItens = getAllListagemAcaoPrevCorretivasUseCase.items;

          // 3. FAZ A DIFERENÇA (Filtra em memória)
          const preventivas = todosItens.filter((x) => x.Tipo === "P");
          const corretivas = todosItens.filter((x) => x.Tipo === "C");

          // 4. Atualiza os Estados das Listas Separadamente
          setAcaoPrevCorretivaLista(preventivas); // Lista de cima (P)
          setAcaoCorretivaLista(corretivas); // Lista de baixo (C)

          // 5. Atualiza Paginação Preventiva
          paginationPrev.setRowsPerPage(
            getAllListagemAcaoPrevCorretivasUseCase.pageSize,
          );
          paginationPrev.setTotalItems(
            getAllListagemAcaoPrevCorretivasUseCase.totalItems,
          );

          // 6. Atualiza Paginação Corretiva (se existir o store)
          if (paginationCorretivas) {
            paginationCorretivas.setRowsPerPage(
              getAllListagemAcaoPrevCorretivasUseCase.pageSize,
            );
            paginationCorretivas.setTotalItems(
              getAllListagemAcaoPrevCorretivasUseCase.totalItems,
            );
          }
        }
      } catch (e: unknown) {
        throw new Error("Falha ao carregar Ações Preventivas e Corretivas!");
      }
    },
    [
      filters.IdAcaoQualidade,
      getAllListagemAcaoPrevCorretivasUseCase,
      paginationCorretivas,
      paginationPrev,
    ],
  );

  const loadAcoesMelhoriaState = useCallback(
    async (IdAcaoQualidade: string) => {
      const Tipo: any = tipo === "Corretiva" ? 1 : 0;
      try {
        await getAllListagemAcoesMelhoriasUseCase.getAllListagemAcoesMelhorias(
          {
            IdAcaoQualidade: Number(IdAcaoQualidade),
            Tipo,
            PesquisarPor: undefined,
            Pesquisar: undefined,
            status: undefined,
            centroDeCusto: [],
          },
          1,
          25,
        );
        setSelectedAcoesPreventiva(
          getAllListagemAcoesMelhoriasUseCase.items[0],
        );
      } catch (e: unknown) {
        throw new Error("Falha ao Ações Melhorias!");
      }
    },
    [getAllListagemAcoesMelhoriasUseCase, tipo],
  );

  //#endregion

  // #region useEffect

  usePageStartBootstrap(async () => {
    try {
      await loadCentroCustoDropDown((list) =>
        useAcoesPreventivasFilters.setState({ centroDeCustoLista: list }),
      );
      if (id) {
        setIdAcaoQualidade(Number(id));
        await loadAcoesMelhoriaState(id);
        await loadAcoesPrevCorretivaListaState({ IdAcaoQualidade: Number(id) });
      }
    } catch (e: unknown) {
      setErrorMessage(processError(e, "Falha ao buscar dados da pagina!")[0]);
    }
  });

  const handleReload = useCallback(async () => {
    try {
      setLoading(true);
      setLoadingPrev(true);
      if (id) {
        loadAcoesMelhoriaState(id);
        loadAcoesPrevCorretivaListaState({ IdAcaoQualidade: Number(id) });
        loadCentroCustoDropDown((list) =>
          useAcoesPreventivasFilters.setState({ centroDeCustoLista: list }),
        );
      }
    } catch (e: unknown) {
      pushErrorNotification(
        "Erro",
        processError(e, "Falha ao buscar dados da pagina!"),
        { isAutoFinish: true },
      );
    } finally {
      setLoading(false);
      setLoadingPrev(false);
    }
  }, [
    id,
    loadAcoesMelhoriaState,
    loadAcoesPrevCorretivaListaState,
    loadCentroCustoDropDown,
    pushErrorNotification,
  ]);
  //#endregion

  // #region Eventos

  //#region Acao Qualidade
  const handleAcoesPreventivaSubmit = useCallback(
    async (form: QualidadeAcoesPreventivasForm) => {
      try {
        setSaving(true);
        setCustomLoading(true);
        if (
          form.IdCentroCusto === undefined ||
          form.DescricaoSetor === undefined ||
          form.Data === undefined
        ) {
          return pushWarnNotification(
            "Erro",
            "Preencha os campos obrigatórios!",
            { isAutoFinish: true },
          );
        }

        const mensagem = form.Tipo === 0 ? "de melhoria" : "corretiva";
        const AcaoAberta = form.AcaoAberta === 1 ? true : false;

        if (idAcaoQualidade) {
          await updateAcoesPreventivasUseCase.updateAcoesPreventivas(
            idAcaoQualidade,
            form.Tipo,
            AcaoAberta,
            form.Data ?? null,
            form.Numero ?? null,
            form.DescricaoProblema ?? null,
            form.AnaliseCausa ?? null,
            form.Abrangencia ?? null,
            form.RequisitoNorma ?? null,
            form.EvidenciaObjetiva ?? null,
            form.IdCentroCusto ?? null,
            form.arquivo1 ?? null,
            form.arquivo2 ?? null,
            form.arquivo3 ?? null,
            form.arquivo4 ?? null,
            form.arquivo5 ?? null,
            form.RealizarAnalise ?? null,
            form.Justificativa ?? null,
            form.Evento ?? null,
            form.Assunto ?? null,
            form.DescricaoSetor ?? null,
            form.IdentificadoPor ?? null,
            form.limparArquivo1 ?? null,
            form.limparArquivo2 ?? null,
            form.limparArquivo3 ?? null,
            form.limparArquivo4 ?? null,
            form.limparArquivo5 ?? null,
          );
          pushInfoNotification("Sucesso", `Ação ${mensagem} Atualizada!`, {
            isAutoFinish: true,
          });
          await loadAcoesMelhoriaState(idAcaoQualidade.toString());
        } else {
          var idAcaoQualidadeResponse =
            await createAcoesPreventivasUseCase.createAcoesPreventivas(
              form.Tipo,
              AcaoAberta,
              form.Data ?? null,
              form.Numero ?? null,
              form.DescricaoProblema ?? null,
              form.AnaliseCausa ?? null,
              form.Abrangencia ?? null,
              form.RequisitoNorma ?? null,
              form.EvidenciaObjetiva ?? null,
              form.IdCentroCusto ?? null,
              form.arquivo1 ?? null,
              form.arquivo2 ?? null,
              form.arquivo3 ?? null,
              form.arquivo4 ?? null,
              form.arquivo5 ?? null,
              form.RealizarAnalise ?? null,
              form.Justificativa ?? null,
              form.Evento ?? null,
              form.Assunto ?? null,
              form.DescricaoSetor ?? null,
              form.IdentificadoPor ?? null,
            );
          if (idAcaoQualidadeResponse && idAcaoQualidadeResponse > 0) {
            setIdAcaoQualidade(idAcaoQualidadeResponse);
            await loadAcoesMelhoriaState(idAcaoQualidadeResponse.toString());

            pushInfoNotification("Sucesso", `Ação ${mensagem} criada!`, {
              isAutoFinish: true,
            });
          } else {
            console.error("Erro: O Backend não retornou um ID válido.");
          }
        }
      } catch (e) {
        pushErrorNotification(
          "Erro",
          processError(e, "Falha ao salvar Ações Preventivas/Corretivas!"),
          { isAutoFinish: true },
        );
      } finally {
        setCustomLoading(false);
        setSaving(false);
      }
    },
    [setCustomLoading, idAcaoQualidade, pushWarnNotification, updateAcoesPreventivasUseCase, pushInfoNotification, loadAcoesMelhoriaState, createAcoesPreventivasUseCase, pushErrorNotification],
  );

  const handleSubmit = hookForm.handleSubmit(
    handleAcoesPreventivaSubmit,
    (errors) => {
      console.log("Validação falhou:", errors);
    },
  );
  //#endregion

  //#region Acao Prev
  const handleAcaoPrevSubmit = useCallback(
    async (form: AcaoPrevCorretivaForm) => {
      try {
        setSaving(true);
        if (form.IdAcaoQualidade && form.IdAcaoQualidade > 0) {
          if (form.IdAcaoPrevCorretiva) {
            await updateAcaoPrevCorretivasUseCase.updateAcaoPrevCorretivas(
              form.IdAcaoPrevCorretiva,
              form.IdAcaoQualidade,
              form.Tipo ?? null,
              form.Numero ?? null,
              form.Descricao ?? null,
              form.IdFuncionarioResponsavel ?? null,
              form.DataPrevista ?? null,
              form.DataRealizado ?? null,
              form.AnaliseEvidencia ?? null,
              form.DataAnalise ?? null,
              form.FoiEficaz ?? null,
              form.IdFuncionarioAnalise ?? null,
            );
          } else {
            await createAcaoPreventivaCorretivaUseCase.createAcaoPreventivaCorretiva(
              form.IdAcaoQualidade,
              form.Tipo ?? null,
              form.Numero ?? null,
              form.Descricao ?? null,
              form.IdFuncionarioResponsavel ?? null,
              form.DataPrevista ?? null,
              form.DataRealizado ?? null,
              form.AnaliseEvidencia ?? null,
              form.DataAnalise ?? null,
              form.FoiEficaz ?? null,
              form.IdFuncionarioAnalise ?? null,
            );
          }
          hookFormPrev.reset();
          await loadAcoesPrevCorretivaListaState({
            IdAcaoQualidade: form.IdAcaoQualidade,
          });
        }
      } catch (e) {
        pushErrorNotification(
          "Erro",
          processError(e, "Falha ao criar Ação Preventivas Corretivas!"),
          { isAutoFinish: true },
        );
      } finally {
        setSaving(false);
      }
    },
    [
      createAcaoPreventivaCorretivaUseCase,
      hookFormPrev,
      loadAcoesPrevCorretivaListaState,
      pushErrorNotification,
      updateAcaoPrevCorretivasUseCase,
    ],
  );

  const handleTablePrevChange = useCallback(
    async (page: number) => {
      try {
        setLoading(true);
        paginationPrev.setPage(page + 1);
        await loadAcoesPrevCorretivaListaState({
          IdAcaoQualidade: idAcaoQualidade,
          page: page + 1,
        });
      } catch (e: unknown) {
        pushErrorNotification(
          "Erro",
          processError(e, "Falha ao buscar dados da pagina!"),
          { isAutoFinish: true },
        );
      } finally {
        setLoading(false);
      }
    },
    [
      idAcaoQualidade,
      loadAcoesPrevCorretivaListaState,
      paginationPrev,
      pushErrorNotification,
    ],
  );

  const handleRowsPerTablePrevChange = useCallback(
    async (rowsPerPage: number) => {
      try {
        setLoading(true);
        paginationPrev.setPage(1);
        paginationPrev.setRowsPerPage(rowsPerPage);

        await loadAcoesPrevCorretivaListaState({
          IdAcaoQualidade: idAcaoQualidade,
          page: 1,
          rowsPerPage,
        });
      } catch (e: unknown) {
        pushErrorNotification(
          "Erro",
          processError(e, "Falha ao buscar dados da pagina!"),
          { isAutoFinish: true },
        );
      } finally {
        setLoading(false);
      }
    },
    [
      idAcaoQualidade,
      loadAcoesPrevCorretivaListaState,
      paginationPrev,
      pushErrorNotification,
    ],
  );

  const handleDeleteConfirmaPrev = useCallback(
    async (IdAcaoPrevCorretiva: number, IdAcaoQualidade: number) => {
      try {
        if (idAcaoQualidade) {
          setSaving(true);
          await deleteAcaoPrevCorretivasUseCase.deleteAcaoPrevCorretivas(
            IdAcaoPrevCorretiva,
            IdAcaoQualidade,
          );
          await loadAcoesPrevCorretivaListaState({
            IdAcaoQualidade: idAcaoQualidade,
          });
        }
      } catch (e: unknown) {
        pushErrorNotification(
          "Erro",
          processError(e, "Falha ao excluir informações!"),
          { isAutoFinish: true },
        );
      } finally {
        setSaving(false);
      }
    },
    [
      idAcaoQualidade,
      deleteAcaoPrevCorretivasUseCase,
      loadAcoesPrevCorretivaListaState,
      pushErrorNotification,
    ],
  );

  const handleVoltarcoesPreventivas = useCallback(
    (tipo: "Corretiva" | "Preventivas") => {
      if (tipo === "Corretiva") navigate(`/AcoesCorretivas_lista/`);
      else if (tipo === "Preventivas") navigate(`/AcoesPreventivas_lista/`);
    },
    [navigate],
  );

  //#endregion

  //#endregion

  //#region ARQUIVOS
  const handleDownloadFile = useCallback(
    async (filename: string, imagePosition: number) => {
      if (id) {
        try {
          setCustomLoading(true);

          await getArquivoUseCase.getArquivo(
            Number(id),
            imagePosition,
            filename,
          );
        } catch (e: unknown) {
          pushErrorNotification(
            "Erro",
            processError(e, "Falha ao fazer download do arquivo!"),
            { isAutoFinish: true },
          );
        } finally {
          setCustomLoading(false);
        }
      }
    },
    [getArquivoUseCase, id, pushErrorNotification, setCustomLoading],
  );

  const handleFileChange = useCallback(
    (fileId: number | null) => {
      if (fileId && selectecoesPreventiva) {
        switch (fileId) {
          case 1:
            setSelectedAcoesPreventiva({
              ...selectecoesPreventiva,
              nomeArquivo1: "",
            });
            hookForm.setValue("limparArquivo1", true);
            break;

          case 2:
            setSelectedAcoesPreventiva({
              ...selectecoesPreventiva,
              nomeArquivo2: "",
            });
            hookForm.setValue("limparArquivo2", true);
            break;

          case 3:
            setSelectedAcoesPreventiva({
              ...selectecoesPreventiva,
              nomeArquivo3: "",
            });
            hookForm.setValue("limparArquivo3", true);
            break;

          case 4:
            setSelectedAcoesPreventiva({
              ...selectecoesPreventiva,
              nomeArquivo4: "",
            });
            hookForm.setValue("limparArquivo4", true);
            break;

          case 5:
            setSelectedAcoesPreventiva({
              ...selectecoesPreventiva,
              nomeArquivo5: "",
            });
            hookForm.setValue("limparArquivo5", true);
            break;
        }
      }
    },
    [selectecoesPreventiva, hookForm],
  );

  //#endregion

  const handleOpenChangeHistory = useCallback(
    (idTabela: number | null, tipoTabela: LogTipoTabela | null) => {
      if (idTabela && tipoTabela) {
        changeHistory.handleOpenChangeHistory(idTabela, tipoTabela, false);
      } else {
        if (idAcaoQualidade) {
          changeHistory.handleOpenChangeHistory(
            idAcaoQualidade,
            LogTipoTabela.ACAO_QUALIDADE,
            true,
          );
        }
      }
    },
    [changeHistory, idAcaoQualidade],
  );

  return {
    loading,
    saving,
    errorMessage,
    openDeleteArquivoModal,
    pagination,
    filters,
    acoesPreventivasLista,
    selectecoesPreventiva,
    hookForm,
    hookFormPrev,
    idAcaoQualidade,
    loadingPrev,
    acaoPrevCorretivaLista,
    acaoCorretivaLista,
    paginationPrev,
    paginationCorretivas,
    changeHistory,
    handleOpenChangeHistory,
    handleReload,
    // handleArquivoDeleteOpenState,
    handleDownloadFile,
    handleSubmit,
    handleAcaoPrevSubmit,
    handleRowsPerTablePrevChange,
    handleTablePrevChange,
    handleDeleteConfirmaPrev,
    handleVoltarcoesPreventivas,
    handleFileChange,
  };
};
